import {createMissionMoment, MISSION_SCRIPT_KINDS} from '../../configs/campaigns/missionFormat';
import {
  checkIfAllMainObjectivesCompleted,
  checkIfLeadingByWinPoints,
  checkMissionObjective,
  findFailedObjective,
} from '../../utils/missions/missionObjectives';
import {
  missionFinished,
  missionScriptAppend,
  missionSetCompletedObjectives,
  missionSetProgress,
  showToast,
} from '../actions';
import {DANGEROUS_ACTION_TYPES} from '../../utils/tutorial/tutorialActionGate';
import {REGISTRY_SINGLETON} from '../../scenes/Registry';

/**
 * Мидлвара сюжетных миссий. Делает две вещи:
 *
 * 1) В РЕЖИМЕ РЕДАКТОРА пишет каждое применённое игровое действие в ленту
 *    миссии вместе с моментом партии (раунд/фаза/этап/чей ход). Это тот же
 *    приём, которым сервер записывает матч для реплея: партия целиком
 *    описывается плоским потоком redux-экшенов.
 *
 * 2) ПРИ ПРОХОЖДЕНИИ (и в редакторе тоже — чтобы автор видел то же, что
 *    увидит игрок) пересчитывает выполненность заданий по состоянию партии
 *    после каждого действия и завершает миссию, когда выполнены все главные
 *    задания или сработало условие поражения.
 */

// Экшены, которые в ленту не пишем: чисто визуальные и приватные
const NON_RECORDED_ACTION_TYPES = new Set([
  'SET_SELECTED_TOKEN',
  'SET_INSPECTED_HEX',
  'SELECT_UNIT',
  'SET_SELECTED_FARM',
  'SET_SELECTED_BARRACKS',
  'TOGGLE_DESTRUCTION_TOOL',
]);

const checkIfRecordableAction = (action) => Boolean(action)
  && typeof action.type === 'string'
  && !action.noServerSync
  && !action.type.startsWith('MISSION_')
  && !NON_RECORDED_ACTION_TYPES.has(action.type);

// Пока на экране реплика персонажа, играть нельзя: сюжет должен быть
// прочитан до конца. Разрешена только инспекция ячеек — она shared-состояние
// не меняет и в список «опасных» экшенов не входит
const BLOCKED_TOAST_THROTTLE_MS = 1500;
let lastBlockedToastAt = 0;

const showReplicaBlockedToast = (store) => {
  const now = Date.now();
  if (now - lastBlockedToastAt < BLOCKED_TOAST_THROTTLE_MS) {
    return;
  }
  lastBlockedToastAt = now;

  store.dispatch(showToast(
    'Сначала прочитай сюжетные реплики — нажми «Далее» под текстом.',
    'info',
    5000,
    true
  ));
  REGISTRY_SINGLETON.getSoundManager()?.playToastError?.();
};

// Защита от рекурсии: пересчёт заданий сам диспатчит экшены
let isEvaluating = false;

const evaluateObjectives = (store) => {
  if (isEvaluating) return;
  isEvaluating = true;

  try {
    const state = store.getState();
    const mission = state.private.mission;
    const playerColor = mission.draft?.playerColor || mission.playerColor;
    if (mission.isMissionCompleted || !playerColor) return;

    const objectives = mission.activeObjectives;
    if (objectives.length === 0) return;

    // Прогресс «лидировать N раундов подряд» — считаем один раз за раунд
    const round = state.shared.gameState.round;
    let progress = mission.progress;
    if (progress.lastLeadingRound !== round) {
      const isLeading = checkIfLeadingByWinPoints(state, playerColor);
      progress = {
        lastLeadingRound: round,
        leadingRounds: isLeading ? (progress.leadingRounds || 0) + 1 : 0,
      };
      store.dispatch(missionSetProgress(progress));
    }

    // Защита в глубину: условия поражения не проверяем, пока партия не дошла
    // до первого раунда — в фазе SETUP города еще не материализованы, и
    // isFailure-цели вида lose_city были бы истинны до первого хода игрока
    const isBeforeFirstRound = round < 1;
    const completedObjectiveIds = objectives
      .filter((objective) => !(objective.isFailure && isBeforeFirstRound))
      .filter((objective) => checkMissionObjective(objective, state, {playerColor, progress}))
      .map((objective) => objective.id);

    const previous = mission.completedObjectiveIds;
    const hasChanged = completedObjectiveIds.length !== previous.length
      || completedObjectiveIds.some((id) => !previous.includes(id));
    if (hasChanged) {
      store.dispatch(missionSetCompletedObjectives(completedObjectiveIds));
    }

    // В редакторе миссию не заканчиваем: автор должен доиграть сценарий до
    // конца и выгрузить конфиг
    if (mission.isEditorMode) return;

    if (findFailedObjective(objectives, completedObjectiveIds)) {
      store.dispatch(missionFinished(true));
      return;
    }
    if (checkIfAllMainObjectivesCompleted(objectives, completedObjectiveIds)) {
      store.dispatch(missionFinished(false));
      return;
    }

    // Раунды кончились, а главные задания не выполнены — это провал миссии.
    // Без этой ветки партия просто заканчивалась подсчётом очков, и игрок
    // сюжетной кампании видел обычный конец матча вместо «Миссия провалена»
    if (state.shared.gameState.gameResults) {
      store.dispatch(missionFinished(true));
    }
  } finally {
    isEvaluating = false;
  }
};

const missionMiddleware = (store) => (next) => (action) => {
  const stateBefore = store.getState();
  const missionBefore = stateBefore.private?.mission;
  const isMissionContext = Boolean(missionBefore) && (missionBefore.isEditorMode || missionBefore.missionId);

  // Гейт реплики — ДО применения экшена: действие игрока не должно пройти,
  // пока герой говорит. Записанные ходы противников (__missionScript) и
  // служебные экшены самой миссии не трогаем; в редакторе тоже не мешаем —
  // там автор сам ставит реплики по ходу записи
  if (isMissionContext
    && !missionBefore.isEditorMode
    && missionBefore.activeReplica
    && !action.__missionScript
    && DANGEROUS_ACTION_TYPES.has(action.type)) {
    showReplicaBlockedToast(store);
    return undefined;
  }

  const result = next(action);

  if (!isMissionContext) {
    return result;
  }

  const state = store.getState();

  // Запись действия в ленту — только в редакторе и только после старта партии
  // (расстановка поля в фазе подготовки уезжает в конфиг снимком, а не лентой)
  if (missionBefore.isEditorMode
    && state.shared.gameState.isStarted
    && checkIfRecordableAction(action)) {
    const moment = createMissionMoment(stateBefore);
    // Ходы живого игрока в ленту НЕ пишем: при прохождении их сыграет сам
    // игрок, и запись только мешала бы. В ленте — только противники
    const isPlayerOwnAction = moment.playerColor
      && moment.playerColor === missionBefore.draft?.playerColor;
    if (isPlayerOwnAction) {
      evaluateObjectives(store);
      return result;
    }
    store.dispatch(missionScriptAppend({
      id: `entry_${state.private.mission.script.length}_${action.type}`,
      kind: MISSION_SCRIPT_KINDS.ACTION,
      playerColor: moment.playerColor,
      moment,
      action: {type: action.type, payload: action.payload},
    }));
  }

  evaluateObjectives(store);

  return result;
};

export default missionMiddleware;
